import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import pickBy from 'lodash/pickBy';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import {
  default as SelectBox,
  GroupedSelectBox
} from '@adsk/bim360-matrix-react-select-box';
import cx from 'classnames';

const SelectBoxAdapter = ({
  options: baseOptions,
  selectedOptions,
  grouped,
  multi,
  clearable,
  searchable,
  valid,
  disabled,
  autofocus,
  labelKey,
  labelGetter,
  valueKey,
  placeholder,
  onChange,
  onOpen,
  onClose,
  searchIcon,
  searchPlaceholder,
  subtextGetter,
  optionRenderer,
  valueRenderer,
  className,
  singleSelectionPrefixList,
  noResultsValue,
  dropdownMaxWidth,
  dropdownMaxHeight,
  containerClassName,
  clearInputOnSelect,
  ['data-testid']: testId,
  key
}) => {
  const isGrouped = grouped === true;
  const formattedOptions = useMemo(
    () => (isGrouped && baseOptions ? baseOptions.map(([label, options]) => ({label, options})) : baseOptions),
    [baseOptions, isGrouped]
  );
  const container = useMemo(() => {
    if (!containerClassName) {
      return undefined;
    }
    const result = document.getElementsByClassName(containerClassName);
    return result?.[0];
  }, [containerClassName]);
  const classNames = cx('Select', {
    [className]: Boolean(className)
  });

  const props = pickBy(
    {
      value: selectedOptions,
      defaultInputValue: '',
      multiple: multi,
      clearable,
      searchable,
      clearInputOnSelect,
      invalid: !isNullOrUndefined(valid) ? !valid : false,
      disabled,
      autoFocus: autofocus,
      labelKey,
      labelGetter,
      valueKey,
      placeholder,
      showSearchIcon: Boolean(searchIcon),
      searchPlaceholder,
      subtextGetter,
      menuClassName: 'SelectBoxAdapter__menu',
      optionRenderer: optionRenderer ? ({value, label}) => optionRenderer(value, label) : undefined,
      valueRenderer: valueRenderer ? ({value, label}) => valueRenderer(value, label) : undefined,
      onChange: value => onChange(value ?? (multi ? [] : null)),
      onOpen,
      onClose,
      className: classNames,
      singleSelectionPrefixList,
      noResultsValue,
      maxMenuWidth: dropdownMaxWidth,
      maxMenuHeight: dropdownMaxHeight,
      container
    },
    value => !isNullOrUndefined(value)
  );

  const Component = grouped ? GroupedSelectBox : SelectBox;

  return (
    <div className="SelectBoxAdapter" data-testid={testId} key={key}>
      <Component {...props} options={formattedOptions} />
    </div>
  );
};

SelectBoxAdapter.displayName = 'SelectBoxAdapter';

SelectBoxAdapter.propTypes = {
  options: PropTypes.array.isRequired,
  selectedOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  grouped: PropTypes.bool,
  multi: PropTypes.bool,
  clearable: PropTypes.bool,
  searchable: PropTypes.bool,
  valid: PropTypes.bool,
  disabled: PropTypes.bool,
  autofocus: PropTypes.bool,
  labelKey: PropTypes.string,
  labelGetter: PropTypes.func,
  valueKey: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  searchIcon: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  subtextGetter: PropTypes.func,
  optionRenderer: PropTypes.func,
  valueRenderer: PropTypes.func,
  className: PropTypes.string,
  singleSelectionPrefixList: PropTypes.arrayOf(PropTypes.any),
  noResultsValue: PropTypes.node,
  dropdownMaxWidth: PropTypes.number,
  dropdownMaxHeight: PropTypes.number,
  containerClassName: PropTypes.string,
  'data-testid': PropTypes.string,
  clearInputOnSelect: PropTypes.bool,
  key: PropTypes.string
};

export default SelectBoxAdapter;
